import React from 'react';
import {
  Save,
  ZoomIn,
  ZoomOut,
  Type,
  Image as ImageIcon,
  Square,
  Circle,
  Loader2,
  Check,
  Undo2,
  Redo2,
  AlignCenterHorizontal,
  AlignCenterVertical,
  AlignStartHorizontal,
  AlignEndHorizontal,
  AlignStartVertical,
  AlignEndVertical,
  Maximize,
  Minimize,
  Package,
  Eye,
  Grid3x3,
} from 'lucide-react';
import { Button } from '../../ui/button';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from '../../ui/tooltip';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectGroup,
  SelectLabel,
  SelectItem,
} from '../../ui/select';
import { FORMAT_PRESETS, getPresetCategories } from './formatPresets';
import { ZOOM_PRESETS } from '../../canvas-shared/canvasConstants';
import type { SocialCanvasElementType } from '../../../services/socialPosting';
import { CanvasBreadcrumb } from '../../canvas-shared/CanvasBreadcrumb';
import type { BreadcrumbSegment } from '../../canvas-shared/CanvasBreadcrumb';

const TOOLS: { type: SocialCanvasElementType; icon: React.ElementType; label: string }[] = [
  { type: 'text', icon: Type, label: 'Text' },
  { type: 'image', icon: ImageIcon, label: 'Image' },
  { type: 'rect', icon: Square, label: 'Rectangle' },
  { type: 'circle', icon: Circle, label: 'Circle' },
];

interface EditorToolbarProps {
  designName: string;
  onDesignNameChange: (name: string) => void;
  zoom: number;
  onZoomChange: (zoom: number) => void;
  onAddElement: (type: SocialCanvasElementType) => void;
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  onSave: () => void;
  isSaving: boolean;
  saveSuccess: boolean;
  onPreview: () => void;
  isPreviewing?: boolean;
  onExportAll?: () => void;
  productCount?: number;
  onBack: () => void;
  canvasWidth: number;
  canvasHeight: number;
  onFormatChange: (width: number, height: number) => void;
  selectedIndex: number | null;
  onAlign: (alignment: string) => void;
  isFullscreen?: boolean;
  onToggleFullscreen?: () => void;
  showGrid?: boolean;
  onToggleGrid?: () => void;
  breadcrumbSegments?: BreadcrumbSegment[];
}

const PRESET_CATEGORIES = getPresetCategories();

export function EditorToolbar({
  designName,
  onDesignNameChange,
  zoom,
  onZoomChange,
  onAddElement,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  onSave,
  isSaving,
  saveSuccess,
  onPreview,
  isPreviewing,
  onExportAll,
  productCount,
  onBack,
  canvasWidth,
  canvasHeight,
  onFormatChange,
  selectedIndex,
  onAlign,
  isFullscreen,
  onToggleFullscreen,
  showGrid,
  onToggleGrid,
  breadcrumbSegments,
}: EditorToolbarProps) {
  const currentPreset = FORMAT_PRESETS.find((p) => p.width === canvasWidth && p.height === canvasHeight);

  return (
    <TooltipProvider delayDuration={300}>
      <div className="shrink-0 border-b bg-muted/30">
        {/* Row 1: Navigation & Actions */}
        <div className="flex items-center gap-2 px-4 py-1.5 border-b border-border/50">
          <CanvasBreadcrumb
            segments={breadcrumbSegments ?? [{ label: 'Designs', onClick: onBack }]}
            editableValue={designName}
            onEditableChange={onDesignNameChange}
          />

          {/* Auto-save indicator */}
          {saveSuccess && (
            <span className="text-xs text-emerald-500 flex items-center gap-1 shrink-0">
              <Check className="h-3 w-3" /> Saved
            </span>
          )}

          <div className="w-px h-6 bg-border shrink-0" />

          {/* Format preset — Shadcn Select grouped by category */}
          <Select
            value={currentPreset?.id || 'custom'}
            onValueChange={(id) => {
              const preset = FORMAT_PRESETS.find((p) => p.id === id);
              if (preset) onFormatChange(preset.width, preset.height);
            }}
          >
            <SelectTrigger className="h-8 w-[180px] min-w-0 text-sm rounded-lg border bg-background">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {!currentPreset && (
                <SelectItem value="custom">{canvasWidth} x {canvasHeight}</SelectItem>
              )}
              {PRESET_CATEGORIES.map((category) => (
                <SelectGroup key={category}>
                  <SelectLabel>{category}</SelectLabel>
                  {FORMAT_PRESETS.filter((p) => p.category === category).map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.label} ({p.width}x{p.height})
                    </SelectItem>
                  ))}
                </SelectGroup>
              ))}
            </SelectContent>
          </Select>

          <div className="flex-1 min-w-0" />

          {/* Save */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className={`rounded-lg shrink-0 ${saveSuccess ? 'border-emerald-500 text-emerald-600' : ''}`}
                onClick={onSave}
                disabled={isSaving}
              >
                {isSaving ? (
                  <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                ) : saveSuccess ? (
                  <Check className="h-4 w-4 mr-1.5 text-emerald-500" />
                ) : (
                  <Save className="h-4 w-4 mr-1.5" />
                )}
                {saveSuccess ? 'Saved' : 'Save'}
              </Button>
            </TooltipTrigger>
            <TooltipContent>Save (Ctrl+S)</TooltipContent>
          </Tooltip>

          {/* Preview */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" size="sm" className="rounded-lg shrink-0" onClick={onPreview} disabled={isPreviewing}>
                {isPreviewing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Eye className="h-4 w-4 mr-1.5" />}
                {isPreviewing ? 'Rendering...' : 'Preview'}
              </Button>
            </TooltipTrigger>
            <TooltipContent>Preview with current product data</TooltipContent>
          </Tooltip>

          {/* Export All */}
          {onExportAll && productCount != null && productCount > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-lg shrink-0"
                  onClick={onExportAll}
                >
                  <Package className="h-4 w-4 mr-1.5" />
                  Export All ({productCount})
                </Button>
              </TooltipTrigger>
              <TooltipContent>Batch export for all products</TooltipContent>
            </Tooltip>
          )}

          {/* Fullscreen */}
          {onToggleFullscreen && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 shrink-0" onClick={onToggleFullscreen}>
                  {isFullscreen ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent>{isFullscreen ? 'Exit Fullscreen (Esc)' : 'Fullscreen'}</TooltipContent>
            </Tooltip>
          )}
        </div>

        {/* Row 2: Tools & Alignment */}
        <div className="flex items-center gap-2 px-4 py-1.5">
          {/* Element tools */}
          <div className="flex items-center gap-0.5">
            {TOOLS.map((tool) => {
              const Icon = tool.icon;
              return (
                <Tooltip key={tool.type}>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => onAddElement(tool.type)}
                      className="h-7 w-7 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                    >
                      <Icon className="h-3.5 w-3.5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>Add {tool.label}</TooltipContent>
                </Tooltip>
              );
            })}
          </div>

          <div className="w-px h-5 bg-border shrink-0" />

          {/* Alignment buttons */}
          <div className="flex items-center gap-0.5">
            {[
              { id: 'left', icon: AlignStartHorizontal, tip: 'Align Left' },
              { id: 'center-h', icon: AlignCenterHorizontal, tip: 'Center Horizontal' },
              { id: 'right', icon: AlignEndHorizontal, tip: 'Align Right' },
              { id: 'top', icon: AlignStartVertical, tip: 'Align Top' },
              { id: 'center-v', icon: AlignCenterVertical, tip: 'Center Vertical' },
              { id: 'bottom', icon: AlignEndVertical, tip: 'Align Bottom' },
            ].map((btn) => {
              const Icon = btn.icon;
              return (
                <Tooltip key={btn.id}>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => onAlign(btn.id)}
                      disabled={selectedIndex === null}
                      className="h-7 w-7 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <Icon className="h-3.5 w-3.5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>{btn.tip}</TooltipContent>
                </Tooltip>
              );
            })}
          </div>

          <div className="flex-1 min-w-0" />

          {/* Undo / Redo */}
          <div className="flex items-center gap-0.5">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={onUndo} disabled={!canUndo}>
                  <Undo2 className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Undo (Ctrl+Z)</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={onRedo} disabled={!canRedo}>
                  <Redo2 className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Redo (Ctrl+Shift+Z)</TooltipContent>
            </Tooltip>
          </div>

          <div className="w-px h-5 bg-border shrink-0" />

          {/* Zoom — Shadcn Select */}
          <div className="flex items-center gap-1 bg-muted/50 rounded-lg p-0.5 shrink-0">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => onZoomChange(Math.max(0.25, zoom - 0.25))}>
                  <ZoomOut className="h-3 w-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Zoom Out</TooltipContent>
            </Tooltip>
            <Select
              value={String(zoom)}
              onValueChange={(v) => onZoomChange(Number(v))}
            >
              <SelectTrigger className="h-6 w-[60px] border-0 bg-transparent text-xs font-medium px-1 focus:ring-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ZOOM_PRESETS.map((z) => (
                  <SelectItem key={z} value={String(z)}>
                    {Math.round(z * 100)}%
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => onZoomChange(Math.min(2, zoom + 0.25))}>
                  <ZoomIn className="h-3 w-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Zoom In</TooltipContent>
            </Tooltip>
          </div>

          <div className="w-px h-5 bg-border shrink-0" />

          {/* Grid toggle */}
          {onToggleGrid && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={showGrid ? 'default' : 'ghost'}
                  size="sm"
                  className="h-7 w-7 p-0 shrink-0"
                  onClick={onToggleGrid}
                >
                  <Grid3x3 className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Toggle Grid</TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
}
